import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DecimalPipe } from '@angular/common';
import { AddInlineEditTableComponent } from '../tables/add-inline-edit-table/add-inline-edit-table.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { TableColumnsProp } from '../../models/tables.model';
import {
  arChargeCodeFormConfig,
  arChargesTableConfig,
} from './ar-charges.config';
import {
  ActionIcons,
  ModalPopupMessages,
  ModeIcons,
  selectionTableNames,
  StatusCodes,
  BulkDeleteItemNames,
  SingleDeleteHeaderText,
  popupWidth,
  CrudActions,
  selectedIdForSummaryTable,
  actionText,
  popupButtons,
  DataTypes,
  ArChargesComponentInputNames,
  validationErrorMessages,
} from '../../utility/constants';
import {
  DefaultInfoHeaderIcons,
  DefaultInfoTableHeaderIcons,
} from '../../utility/svg-constants';
import {
  FormConfig,
  IDropdownResponse,
  SvgIcon,
  SvgIconProp,
} from '../../models/dynamic.model';
import { CommonService } from '../../services/common.service';
import {
  ArChargesFormObject,
  ArChargesService,
  SyncApChargesEligibility,
  UnpublishedArInvoice,
} from './ar-charges.service';
import { ModalService } from '../../services/modal.service';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { FormArray, FormGroup } from '@angular/forms';
import { toApiDateString } from '../../utility/invoice-date.util';
import { SnakbarService } from '../../services/snakbar.service';
import { MessagePopupComponent } from '../message-popup/message-popup.component';
import { InformationNoteComponent } from '../information-note/information-note.component';
import {
  combineLatest,
  concat,
  distinctUntilChanged,
  filter,
  Observable,
  tap,
  toArray,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { RpeSharedTabBinding } from '../../rpe/rpe-shared-tab.binding';
import { RpeRuntimeBridgeService } from '../../services/rpe-runtime-bridge.service';
import { KeyStrokeDirectivesModule } from '../../directives/keystroke/keystrokes.module';
import { CommonPopupService } from '../../services/common-popup.service';
import {
  arChargeDraftRowHasData,
  chargeGridHasActionableValidationErrors,
  AR_SHIPMENT_TAB_REQUIRED_KEYS,
  getEditableChargeRowIndexesWithData,
  markInvalidChargeRows,
  resolveChargeRowDisabledOnPatch,
  withGridSaveForViewModeOnly,
} from '../charge-grid-save-validation.util';

const AR_INVOICE_NUMBER_KEY = 'invoiceNumber';
const AR_INVOICE_ID_KEY = 'arInvoiceId';

@Component({
  selector: 'app-ar-charges',
  standalone: true,
  imports: [
    AddInlineEditTableComponent,
    SvgIconComponent,
    CommonModule,
    DecimalPipe,
    InformationNoteComponent,
    KeyStrokeDirectivesModule,
  ],
  templateUrl: './ar-charges.component.html',
  styleUrl: './ar-charges.component.scss',
})
export class ArChargesComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild(AddInlineEditTableComponent, { static: false })
  private readonly addInlineEditTableComp!: AddInlineEditTableComponent;

  @Input() arChargesCrudLocked = false;
  @Input() rpeGridBinding: RpeSharedTabBinding | null = null;
  @Output() arChargeSaved = new EventEmitter<void>();
  @Output() invoiceNumberClicked = new EventEmitter<any>();
  /** PAC-24768 AC1: Invoice Number is a type-ahead over unpublished AR invoices (inline-table generic autocomplete). */
  public arChargesExternalDropdownData: {
    [key: string]: IDropdownResponse[];
  } = {};
  public readonly arChargesExternalAutocompleteKeys: string[] = [
    AR_INVOICE_NUMBER_KEY,
  ];
  public readonly arChargesExternalApiControlNames: {
    [key: string]: string;
  } = { [AR_INVOICE_NUMBER_KEY]: AR_INVOICE_ID_KEY };
  private unpublishedArInvoicesById = new Map<number, UnpublishedArInvoice>();
  private syncApEligibility: SyncApChargesEligibility | null = null;
  private isSyncingApCharges = false;
  public tableColumnConfig: TableColumnsProp[] = arChargesTableConfig;
  public tableName = selectionTableNames;
  public arChargesFormConfig: FormConfig = arChargeCodeFormConfig;
  public arChargesHeaderIcons!: SvgIconProp[];
  public pageMode!: string;
  public arChargesGridForm!: FormGroup;
  public arChargesGridFormData: any;
  public arChargesSelectedRow: any[] = [];
  public arChargesData: ArChargesFormObject[] = [];
  public totalChargeAmount = 0;
  public showTotalRow = false;
  public readonly hawbArChargesBlockedNote =
    ModalPopupMessages.hawbArChargesBlockedNote;
  private readonly crudHeaderActionIcons = new Set<string>([
    ActionIcons.add,
    ActionIcons.edit,
    ActionIcons.delete,
  ]);

  public arChargesButtonClickHandler: Record<
    string,
    () => void | Promise<void>
  > = {
    [ActionIcons.add]: this.handleARAddClicked.bind(this),
    [ActionIcons.edit]: this.handleAREditClicked.bind(this),
    [ActionIcons.delete]: this.handleARDeleteClicked.bind(this),
    [ActionIcons.close]: this.handleARCancelClicked.bind(this),
    [ActionIcons.save]: this.handleARSaveClicked.bind(this),
    [ActionIcons.syncApInvoices]: this.handleSyncApInvoicesClicked.bind(this),
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly rpeRuntimeBridge = inject(RpeRuntimeBridgeService);
  public isAddClicked = false;
  public isEditClicked = false;
  private hasPatched = false;
  private deletedArCharges: ArChargesFormObject[] = [];
  private isInternalUpdate = false;

  constructor(
    private readonly commonService: CommonService,
    private readonly arChargesService: ArChargesService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly modalService: ModalService,
    private readonly commonPopupService: CommonPopupService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly snakbarService: SnakbarService
  ) {}

  ngOnDestroy(): void {
    this.persistChargeGridStateOnTabLeave();
    this.syncChargeGridValidationBlockedState();
  }

  /** Sync draft rows to the shared service before the dynamic tab component is destroyed. */
  private persistChargeGridStateOnTabLeave(): void {
    if (this.pageMode === ModeIcons.view || !this.arChargesGridForm) {
      return;
    }
    const rows = this.getArChargesFormRows();
    if (!rows?.length) {
      return;
    }
    this.syncFormDataToCollection();
  }

  /** Used by Shipment/MAWB main save and AR tab save when the grid has invalid charge rows. */
  public validateChargeGridForSave(showPopup = true): boolean {
    if (!this.arChargesGridForm) {
      return true;
    }
    if (
      this.pageMode === ModeIcons.view &&
      !this.isAddClicked &&
      !this.isEditClicked
    ) {
      this.arChargesService.setChargeGridValidationBlocked(false);
      return true;
    }
    // Marks empty mandatory fields of filled draft rows so the grid shows their inline error icon.
    const hasMissingRequired = markInvalidChargeRows(
      this.getArChargesFormRows(),
      this.getArDraftRowIndexesWithData(),
      () => AR_SHIPMENT_TAB_REQUIRED_KEYS
    );
    const hasErrors =
      hasMissingRequired || this.chargeGridHasActionableValidationErrors();
    this.arChargesService.setChargeGridValidationBlocked(hasErrors);
    if (!hasErrors) {
      return true;
    }
    this.changeDetectorRef.detectChanges();
    if (showPopup) {
      this.commonPopupService.createErrorPopupData(
        this.modalService,
        validationErrorMessages.arModuleChargeValidation,
        ModalPopupMessages.Validation
      );
    }
    return false;
  }

  private syncChargeGridValidationBlockedState(): void {
    if (
      !this.arChargesGridForm ||
      (this.pageMode === ModeIcons.view &&
        !this.isAddClicked &&
        !this.isEditClicked)
    ) {
      this.arChargesService.setChargeGridValidationBlocked(false);
      return;
    }
    this.arChargesService.setChargeGridValidationBlocked(
      this.chargeGridHasActionableValidationErrors()
    );
  }

  private chargeGridHasActionableValidationErrors(): boolean {
    return chargeGridHasActionableValidationErrors(
      this.getArChargesFormRows(),
      this.pageMode,
      arChargeDraftRowHasData
    );
  }

  private getArDraftRowIndexesWithData(): number[] {
    return getEditableChargeRowIndexesWithData(
      this.getArChargesFormRows(),
      arChargeDraftRowHasData
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes[ArChargesComponentInputNames.crudLocked] &&
      this.pageMode &&
      this.arChargesHeaderIcons?.length
    ) {
      this.getIconsForArChargesTableMode(this.pageMode);
      this.updateIconsBasedOnSelection(this.arChargesSelectedRow.length > 0);
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnInit(): void {
    this.checkMode();
    this.rpeRuntimeBridge.contextRefresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyRpeFieldPermissions();
        if (this.pageMode) {
          this.getIconsForArChargesTableMode(this.pageMode);
        }
      });
    combineLatest([
      this.arChargesService.dropdownsLoaded$,
      this.arChargesService.getARTabDetailsCollection$,
    ])
      .pipe(
        filter(([dropdownsLoaded, data]) => dropdownsLoaded && data !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([dropdownsLoaded, data]) => {
        this.arChargesData = data;
        this.hasPatched = false;
        this.tryPatchArChargesData();
        // Collection also re-emits on our own syncFormDataToCollection — skip those.
        if (!this.isInternalUpdate) {
          this.loadUnpublishedArInvoices();
          this.refreshSyncApEligibility();
        }
      });
  }

  ngAfterViewInit(): void {
    this.arChargesGridForm = this.addInlineEditTableComp.inlineCrudTableForm;
    this.tryPatchArChargesData();
    this.applyRpeFieldPermissions();
  }

  private applyRpeFieldPermissions(): void {
    if (!this.rpeGridBinding || !this.arChargesGridForm) {
      return;
    }
    this.rpeGridBinding.applyToForm(this.arChargesGridForm);
  }

  private tryPatchArChargesData(): void {
    if (
      this.arChargesGridForm &&
      !this.hasPatched &&
      this.arChargesData?.length >= 0
    ) {
      this.hasPatched = true;
      this.patchArChargesData();
    }
  }

  public onARChargesGridFormFieldChange(formValue: any): void {
    this.arChargesGridFormData = formValue;
    this.syncInvoiceFieldsFromTypeahead();
    this.updateTotalRow();
    // Grid Save (view mode only) is enabled once any draft row has a value; rows are validated on click.
    const hasDraftRowData = this.getArDraftRowIndexesWithData().length > 0;
    if (hasDraftRowData && !this.isViewLikePageMode(this.pageMode)) {
      this.commonService.checkPristineAccordionData(false);
    }
    this.arChargesHeaderIcons = this.arChargesHeaderIcons.map(
      (icon: SvgIconProp) => {
        if (icon.name === ActionIcons.save) {
          icon.isDisabled = !hasDraftRowData;
        }
        return icon;
      }
    );
  }

  public onGridDatePickerChange({
    rowIndex,
    key,
  }: {
    rowIndex: number;
    key: string;
  }): void {
    const rows = this.getArChargesFormRows();
    const invDate = toApiDateString(rows.at(rowIndex).get(key)?.value);
    if (key !== 'invoiceDate' || !invDate) {
      return;
    }
    this.arChargesService.setShipmentInvoicedDate(invDate);
    rows.controls.forEach(row =>
      this.arChargesService.patchUnInvoicedInvoiceDate(row)
    );
  }

  public onARChargesTableSelectionChange(selectedRows: any[]): void {
    this.arChargesSelectedRow = selectedRows;
    this.updateIconsBasedOnSelection(selectedRows.length > 0);
  }

  public onInvoiceNumberClick(event: any): void {
    this.invoiceNumberClicked.emit(event);
  }

  public onARChargesIconClicked(iconClicked: SvgIcon): void {
    if (this.arChargesButtonClickHandler[iconClicked.name]) {
      this.arChargesButtonClickHandler[iconClicked.name]();
    }
  }

  private handleARAddClicked(): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    if (this.arChargesCrudLocked) {
      return;
    }
    this.appendEmptyArChargeRowFromAddAction();
  }

  /** FR-041 — View-mode AR mutate only when flag off / super admin. */
  private isViewModeChargesMutateBlocked(
    mode: string = this.pageMode
  ): boolean {
    return (
      this.isViewLikePageMode(mode) &&
      !this.rpeRuntimeBridge.canMutateInformationTabsInViewMode()
    );
  }

  private isViewLikePageMode(mode: string): boolean {
    const normalized = (mode ?? '').toString().toLowerCase();
    return (
      mode === ModeIcons.view ||
      mode === ModeIcons.smallview ||
      normalized === 'view'
    );
  }

  public onAppendChargeRowFromKeyboard(event: { rowIndex: number }): void {
    if (this.arChargesCrudLocked || this.isViewModeChargesMutateBlocked()) {
      return;
    }
    const rowIndex = event.rowIndex;
    const rows = this.getArChargesFormRows();
    const row = rows.at(rowIndex);
    if (!row || row.get('isRowDisable')?.value) {
      return;
    }
    this.isAddClicked = true;
    this.insertArChargeRowAt(0);
    this.focusArChargesRow(0);
  }

  /** Add / Alt+A: insert another empty editable row at the top without clearing existing rows. */
  private appendEmptyArChargeRowFromAddAction(): void {
    this.isAddClicked = true;
    this.getIconsForAddIconClicked();
    const rows = this.getArChargesFormRows();
    if (!rows.length) {
      this.addInlineEditTableComp.addNewRow();
      this.addInlineEditTableComp.getRows().at(0).patchValue({
        isRowDisable: false,
        action: 'create',
        tempId: this.commonService.generateUniqueTempId(),
        id: 0,
      });
      this.inputRowChanges(0);
      this.addInlineEditTableComp.loadAutocompleteForNewRow(0);
    } else {
      this.insertArChargeRowAt(0);
      this.focusArChargesRow(0);
    }
    this.addInlineEditTableComp.getTableData();
    this.changeDetectorRef.detectChanges();
    this.applyRpeFieldPermissions();
    this.commonService.checkPristineAccordionData(false);
  }

  /** Add / Tab: insert an empty editable row without rebuilding the grid (preserves other rows). */
  private insertArChargeRowAt(insertIndex: number): void {
    this.addInlineEditTableComp.insertNewRowAt(insertIndex);
    this.addInlineEditTableComp.getRows().at(insertIndex).patchValue({
      isRowDisable: false,
      action: 'create',
      tempId: this.commonService.generateUniqueTempId(),
      id: 0,
    });
    this.inputRowChanges(insertIndex);
    this.addInlineEditTableComp.loadAutocompleteForNewRow(insertIndex);
    this.addInlineEditTableComp.getTableData();
    this.changeDetectorRef.detectChanges();
    this.applyRpeFieldPermissions();
    this.commonService.checkPristineAccordionData(false);
  }

  private focusArChargesRow(rowIndex: number): void {
    const fieldKey =
      this.addInlineEditTableComp.getFirstFocusableChargeColumnKey();
    this.changeDetectorRef.detectChanges();
    requestAnimationFrame(() => {
      this.addInlineEditTableComp.focusField(rowIndex, fieldKey);
    });
  }

  public addNewRow(): void {
    if (this.arChargesCrudLocked) {
      return;
    }
    this.isAddClicked = true;
    this.getIconsForAddIconClicked();
    const formArray = this.getArChargesFormRows();
    const existingData = formArray.value;
    formArray.clear();
    this.addInlineEditTableComp.addNewRow();
    this.addInlineEditTableComp.loadAutocompleteForNewRow(0);
    if (existingData && existingData.length > 0) {
      existingData.forEach((data: any, index: number) => {
        this.addInlineEditTableComp.addNewRow();
        const newIndex = index + 1;
        formArray.at(newIndex).patchValue(data);
        formArray.at(newIndex).disable();
        this.addInlineEditTableComp.loadAutocompleteForNewRow(newIndex);
      });
    }
    this.inputRowChanges(0);
    this.addInlineEditTableComp.getTableData();
    this.changeDetectorRef.detectChanges();
    this.applyRpeFieldPermissions();
  }

  private getIconsForAddIconClicked() {
    const arChargesHeaderIcons = structuredClone(DefaultInfoHeaderIcons);
    const actionIconNames = new Set([
      ActionIcons.add,
      ActionIcons.edit,
      ActionIcons.delete,
      ActionIcons.save,
      ActionIcons.close,
    ]);
    this.arChargesHeaderIcons = withGridSaveForViewModeOnly(
      this.appendSyncApInvoicesIcon(
        arChargesHeaderIcons
          .filter((val: SvgIconProp) => actionIconNames.has(val.name))
          .map((val: SvgIconProp) => {
            val.isDisabled = this.isChargeGridAddFlowIconDisabled(val.name);
            return val;
          }),
        this.pageMode
      ),
      this.isViewLikePageMode(this.pageMode)
    );
    this.applyBlockedAutoRatedCrudLock();
    this.applySyncApInvoicesIconState();
  }

  /** During add flow: keep Add and Close enabled so users can insert multiple rows. */
  private isChargeGridAddFlowIconDisabled(iconName: string): boolean {
    if (iconName === ActionIcons.add || iconName === ActionIcons.close) {
      return false;
    }
    if (iconName === ActionIcons.save) {
      return true;
    }
    return true;
  }

  private updateIconsBasedOnSelection(hasSelection: boolean): void {
    const hasPublishedSelection = this.arChargesSelectedRow.some(row =>
      this.isPublishedToGp(row)
    );
    this.arChargesHeaderIcons = this.arChargesHeaderIcons.map(
      (icon: SvgIconProp) =>
        this.resolveIconState(icon, hasSelection, hasPublishedSelection)
    );
  }

  private resolveIconState(
    icon: SvgIconProp,
    hasSelection: boolean,
    hasPublishedSelection: boolean
  ): SvgIconProp {
    if (this.applyLockedCrudIconState(icon)) {
      return icon;
    }
    switch (icon.name) {
      case ActionIcons.delete:
        return this.resolveDeleteIcon(
          icon,
          hasSelection,
          hasPublishedSelection
        );
      case ActionIcons.edit:
        return this.resolveEditIcon(icon, hasPublishedSelection);
      case ActionIcons.add:
        return this.resolveAddIcon(icon, hasSelection);
      case ActionIcons.save:
        return this.resolveSaveIcon(icon, hasSelection);
      case ActionIcons.close:
        return this.resolveCloseIcon(icon, hasSelection);
      default:
        return icon;
    }
  }

  private resolveDeleteIcon(
    icon: SvgIconProp,
    hasSelection: boolean,
    hasPublishedSelection: boolean
  ): SvgIconProp {
    icon.isDisabled = !hasSelection || hasPublishedSelection;
    icon.tooltip = hasPublishedSelection
      ? ModalPopupMessages.ARShipmentPublishedWarning(actionText.deleted)
      : undefined;
    return icon;
  }

  private resolveEditIcon(
    icon: SvgIconProp,
    hasPublishedSelection: boolean
  ): SvgIconProp {
    icon.isDisabled =
      this.arChargesSelectedRow.length !== 1 || hasPublishedSelection;
    icon.tooltip = hasPublishedSelection
      ? ModalPopupMessages.ARShipmentPublishedWarning(actionText.modified)
      : undefined;
    return icon;
  }

  private resolveAddIcon(
    icon: SvgIconProp,
    hasSelection: boolean
  ): SvgIconProp {
    icon.isDisabled = hasSelection;
    return icon;
  }

  private resolveSaveIcon(
    icon: SvgIconProp,
    hasSelection: boolean
  ): SvgIconProp {
    if (this.pageMode !== ModeIcons.view && hasSelection) {
      icon.isDisabled = true;
    }
    return icon;
  }

  private resolveCloseIcon(
    icon: SvgIconProp,
    hasSelection: boolean
  ): SvgIconProp {
    if (hasSelection) {
      icon.isDisabled = true;
    } else {
      const hasEditableRow = this.hasEditableRow();
      icon.isDisabled =
        this.pageMode === ModeIcons.view ? !hasEditableRow : false;
    }
    return icon;
  }

  private hasEditableRow(): boolean {
    return this.getArChargesFormRows()?.controls.some(
      control => !control.get('isRowDisable')?.value
    );
  }

  private isPublishedToGp(row: any): boolean {
    const publishedVal = row?.published;
    const normalized =
      typeof publishedVal === DataTypes.String
        ? publishedVal.trim().toLowerCase()
        : publishedVal;
    const isPublished =
      normalized === true || normalized === popupButtons.smallYes;
    const hasInvoiceNumber = !!row?.invoiceNumber;
    return isPublished && hasInvoiceNumber;
  }

  private getIconsForArChargesTableMode(mode: string): void {
    const arChargesHeaderIcons = structuredClone(DefaultInfoTableHeaderIcons);
    const filteredIconList: SvgIconProp[] = [];
    const isBulkEditContext = this.isBulkEditContextFromUrl();
    const isBulkEditViewMode = this.commonService.isBulkEditViewMode.value;
    const isBulkEditMode =
      isBulkEditContext &&
      (isBulkEditViewMode ||
        mode === ModeIcons.bulkEdit ||
        this.pageMode === ModeIcons.bulkEdit);

    if (isBulkEditMode) {
      arChargesHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.name !== ActionIcons.view) {
          filteredIconList.push(val);
        }
      });
    } else if (mode === ModeIcons.add || mode === ModeIcons.edit) {
      arChargesHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.name === ActionIcons.view) {
          return;
        }
        if (val.name === ActionIcons.add) {
          val.isDisabled = false;
        } else if (val.name === ActionIcons.save) {
          val.isDisabled = true;
        }
        filteredIconList.push(val);
      });
    } else if (this.isViewLikePageMode(mode)) {
      if (this.isViewModeChargesMutateBlocked(mode)) {
        this.arChargesHeaderIcons = [];
        this.changeDetectorRef.detectChanges();
        return;
      }
      arChargesHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.name === ActionIcons.add) {
          val.isDisabled = false;
        } else if (
          val.name === ActionIcons.edit ||
          val.name === ActionIcons.delete ||
          val.name === ActionIcons.save ||
          val.name === ActionIcons.close
        ) {
          val.isDisabled = true;
        }
        if (val.name !== ActionIcons.view) {
          filteredIconList.push(val);
        }
      });
    }

    this.arChargesHeaderIcons = withGridSaveForViewModeOnly(
      isBulkEditMode
        ? filteredIconList
        : this.appendSyncApInvoicesIcon(filteredIconList, mode),
      this.isViewLikePageMode(this.pageMode)
    );

    if (isBulkEditMode) {
      this.arChargesHeaderIcons = this.arChargesHeaderIcons.map(
        (icon: SvgIconProp) => {
          icon.isDisabled = true;
          return icon;
        }
      );
      this.changeDetectorRef.detectChanges();
    } else {
      const disabledIcons =
        this.commonService.disableAllIconsInBulkEditViewMode(
          this.pageMode,
          this.arChargesHeaderIcons
        );
      if (disabledIcons) {
        this.arChargesHeaderIcons = disabledIcons;
        this.changeDetectorRef.detectChanges();
      }
    }
    this.applyBlockedAutoRatedCrudLock();
    this.applySyncApInvoicesIconState();
  }

  private applyBlockedAutoRatedCrudLock(): void {
    if (!this.arChargesCrudLocked || !this.arChargesHeaderIcons) {
      return;
    }
    for (const icon of this.arChargesHeaderIcons) {
      this.applyLockedCrudIconState(icon);
    }
  }

  private applyLockedCrudIconState(icon: SvgIconProp): boolean {
    if (
      !this.arChargesCrudLocked ||
      !this.crudHeaderActionIcons.has(icon.name)
    ) {
      return false;
    }
    icon.isDisabled = true;
    return true;
  }

  private isBulkEditContextFromUrl(): boolean {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const mode = urlParams.get('mode');
    return mode === ActionIcons.bulkEdit;
  }

  private handleAREditClicked(): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    if (this.arChargesCrudLocked) {
      return;
    }
    if (this.arChargesSelectedRow.some(row => this.isPublishedToGp(row))) {
      return;
    }
    this.isEditClicked = true;
    this.arChargesSelectedRow.forEach((charge: any) => {
      const param =
        charge.id === 0
          ? selectedIdForSummaryTable.tempId
          : selectedIdForSummaryTable.Id;
      this.getArChargesFormRows().controls.forEach((control, index) => {
        if (control.value[param] === charge[param]) {
          this.getArChargesFormRows()
            .at(index)
            .get('isRowDisable')
            ?.setValue(false);
          this.addInlineEditTableComp.loadAutocompleteForNewRow(index);
          this.inputRowChanges(index);
        }
      });
    });
    this.addInlineEditTableComp.inlineTableSelection.clear();
    this.getIconsForArChargesTableMode(ModeIcons.add);
    this.applyRpeFieldPermissions();
  }

  private handleARDeleteClicked(): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    if (this.arChargesCrudLocked) {
      return;
    }
    if (this.arChargesSelectedRow.some(row => this.isPublishedToGp(row))) {
      return;
    }
    let message: string;
    let messageType: string;
    if (this.arChargesSelectedRow.length > 1) {
      message = ModalPopupMessages.bulkDeleteMessage(
        this.arChargesSelectedRow.length,
        BulkDeleteItemNames.arCharges
      );
      messageType = ModalPopupMessages.BulkDeleteHeader;
    } else {
      message =
        ModalPopupMessages.singleDeleteMessage(
          SingleDeleteHeaderText.arCharges
        ) + '?';
      messageType = ModalPopupMessages.singleDeleteHeader(
        SingleDeleteHeaderText.arCharges
      );
    }
    const dialogRef = this.modalService.openPopup(
      {
        message: message,
        isMultiple: true,
        isFooterRequired: true,
        isBulk: this.arChargesSelectedRow.length > 1,
      },
      messageType,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.deleteArCharges();
        }
      });
  }

  private deleteArCharges(): void {
    const selectedForDelete = [...this.arChargesSelectedRow];
    selectedForDelete.forEach((charge: any) => {
      const param =
        this.commonService.isValidField(charge.id) && charge.id !== 0
          ? selectedIdForSummaryTable.Id
          : selectedIdForSummaryTable.tempId;
      const rows = this.getArChargesFormRows();
      const indexToRemove: number[] = [];
      rows.controls.forEach((control, index) => {
        if (control.value[param] === charge[param]) {
          indexToRemove.push(index);
        }
      });
      indexToRemove.sort((a, b) => b - a);
      indexToRemove.forEach(index => {
        const rowToDelete = rows.at(index).getRawValue();
        if (param === selectedIdForSummaryTable.Id) {
          rowToDelete.action = CrudActions.delete;
          const parentDetails = this.arChargesService.getDetailsForARTab();
          if (!parentDetails.isFromHawb) {
            rowToDelete.isFromShipment = false;
            rowToDelete.shipmentIdOrMawbId = parentDetails.id ?? 0;
          }
          this.deletedArCharges.push(rowToDelete);
        }
        rows.removeAt(index);
      });
      this.addInlineEditTableComp.getTableData();
    });
    if (this.pageMode === ModeIcons.view) {
      this.deleteArChargesInViewMode(selectedForDelete);
    } else {
      this.getIconsForArChargesTableMode(ModeIcons.add);
      this.addInlineEditTableComp.inlineTableSelection.clear();
      this.syncFormDataToCollection();
      this.commonService.checkPristineAccordionData(false);
    }
  }

  private deleteArChargesInViewMode(selectedForDelete: any[]): void {
    const deletedArCharges =
      this.arChargesService.buildPayloadForDeleteARChargeInViewMode(
        selectedForDelete
      );
    this.arChargesService
      .deleteArCharge(deletedArCharges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode === StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              data.message,
              ModalPopupMessages.success
            );
            this.addInlineEditTableComp.inlineTableSelection.clear();
            this.arChargeSaved.emit();
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private handleARCancelClicked(): void {
    this.isAddClicked = false;
    this.isEditClicked = false;
    this.deletedArCharges = []; // Clear deleted charges on cancel
    this.arChargesService.revertShipmentInvoicedDate();
    this.getArChargesFormRows().at(0).reset();
    this.getArChargesFormRows().at(0).enable();
    if (this.pageMode === ModeIcons.view) {
      this.getIconsForArChargesTableMode(ModeIcons.view);
    } else {
      this.getArChargesFormRows().at(0).enable();
      this.getIconsForArChargesTableMode(ModeIcons.add);
    }
    this.hasPatched = false;
    this.patchArChargesData();
    if (this.pageMode === ModeIcons.view) {
      this.arChargesService.resetUnsavedChanges();
      this.commonService.checkPristineAccordionData(true);
      this.snakbarService.openCustomisedSnackBar(
        ModalPopupMessages.actionCancel,
        ModalPopupMessages.success
      );
    }
  }

  private handleARSaveClicked(): void {
    if (this.isViewModeChargesMutateBlocked()) {
      return;
    }
    if (this.pageMode !== ModeIcons.view) {
      if (!this.validateChargeGridForSave(true)) {
        return;
      }
      this.isEditClicked ? this.saveAREditedData() : this.saveARAddedData();
    } else if (this.pageMode === ModeIcons.view) {
      if (!this.validateChargeGridForSave(true)) {
        return;
      }
      this.saveArDraftRowsInViewMode();
    }
  }

  /**
   * View mode (no Shipment/MAWB Save): persist every draft row with values —
   * new rows via create, edited rows via update; sent sequentially.
   */
  private saveArDraftRowsInViewMode(): void {
    const rows = this.getArChargesFormRows();
    const draftRows = this.getArDraftRowIndexesWithData().map(index =>
      rows.at(index).getRawValue()
    );
    const isNewRow = (row: any) => !(+(row.id || 0) > 0);
    const newRows = draftRows.filter(isNewRow);
    const editedRows = draftRows.filter(row => !isNewRow(row));
    const requests: Observable<any>[] = [];
    if (newRows.length) {
      requests.push(
        this.arChargesService.createArCharge(
          this.arChargesService.buildPayloadForSaveOrUpdateARChargeInViewMode(
            newRows,
            true
          )
        )
      );
    }
    if (editedRows.length) {
      requests.push(
        this.arChargesService.updateArCharge(
          this.arChargesService.buildPayloadForSaveOrUpdateARChargeInViewMode(
            editedRows,
            false
          )
        )
      );
    }
    if (!requests.length) {
      return;
    }
    let savedCount = 0;
    concat(...requests)
      .pipe(
        tap(response => {
          if (response?.statusCode === StatusCodes.Success) {
            savedCount++;
          }
        }),
        toArray(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (responses: any[]) => {
          const failed = responses.find(
            response => response?.statusCode !== StatusCodes.Success
          );
          if (failed) {
            // Other error codes already get a red toast from errorHandler().
            if (failed.statusCode === StatusCodes.SuccessfulRequest) {
              this.snakbarService.openCustomisedSnackBar(
                failed.message,
                ModalPopupMessages.error
              );
            }
          } else {
            this.isAddClicked = false;
            this.isEditClicked = false;
            this.snakbarService.openCustomisedSnackBar(
              responses[responses.length - 1]?.message,
              ModalPopupMessages.success
            );
          }
          // Refresh only when something was persisted, so a failed save keeps the user's rows.
          if (savedCount > 0) {
            this.arChargeSaved.emit();
          }
        },
        // Red error toast is shown by errorHandler() in the service call.
        error: () => {
          if (savedCount > 0) {
            this.arChargeSaved.emit();
          }
        },
      });
  }

  private saveAREditedData() {
    if (!this.arChargesGridForm.valid) {
      this.validateChargeGridForSave(true);
      return;
    }
    if (this.arChargesGridForm.valid) {
      this.arChargesService.commitShipmentInvoicedDate();
      this.getArChargesFormRows().at(0).enable();
      this.arChargesService.applyInvoiceDateState(this.getArChargesFormRows().at(0));
      this.getArChargesFormRows().controls.forEach((control, index) => {
        if (index > 0 && control.value.isRowDisable === false) {
          const editedRow = control.getRawValue();
          editedRow.isRowDisable = true;
          control.patchValue(editedRow);
          control.disable();
        }
      });
      this.isEditClicked = false;
      this.addInlineEditTableComp.inlineTableSelection.clear();
      this.addInlineEditTableComp.getTableData();
      this.getIconsForArChargesTableMode(this.pageMode);
      this.syncFormDataToCollection();
      this.commonService.checkPristineAccordionData(false);
      this.arChargesService.setChargeGridValidationBlocked(false);
    }
  }

  private saveARAddedData() {
    if (!this.arChargesGridForm.valid) {
      this.validateChargeGridForSave(true);
      return;
    }
    if (this.arChargesGridForm.valid) {
      this.arChargesService.commitShipmentInvoicedDate();
      this.addInlineEditTableComp.addNewRow();
      const newRow = this.getArChargesFormRows().at(0).getRawValue();
      newRow.isRowDisable = true;
      newRow.tempId = this.commonService.generateUniqueTempId();
      this.addInlineEditTableComp.loadAutocompleteForNewRow(
        this.getArChargesFormRows().length - 1
      );
      this.getArChargesFormRows()
        .at(this.getArChargesFormRows().length - 1)
        .patchValue(newRow);
      this.getArChargesFormRows()
        .at(this.getArChargesFormRows().length - 1)
        .disable();
      this.getArChargesFormRows().at(0).reset();
      this.arChargesService.applyInvoiceDateState(this.getArChargesFormRows().at(0));
      this.arChargesHeaderIcons = this.arChargesHeaderIcons.map(
        (icon: SvgIconProp) => {
          if (icon.name === ActionIcons.save) {
            icon.isDisabled = true;
          }
          return icon;
        }
      );
      this.addInlineEditTableComp.getTableData();
      this.changeDetectorRef.detectChanges();
      this.syncFormDataToCollection();
      this.commonService.checkPristineAccordionData(false);
      this.arChargesService.setChargeGridValidationBlocked(false);
    }
  }

  private getArChargesFormRows(): FormArray {
    return this.arChargesGridForm.get('rows') as FormArray;
  }

  public syncFormDataToCollection(): void {
    const formRows = this.getArChargesFormRows();
    const allCharges: ArChargesFormObject[] = [];
    const parentDetails = this.arChargesService.getDetailsForARTab();
    formRows.controls.forEach(control => {
      const rowData = control.getRawValue();
      if (
        this.pageMode !== ModeIcons.view &&
        control.enabled &&
        !arChargeDraftRowHasData(rowData)
      ) {
        return; // Skip blank editable rows
      }
      if (control.enabled) {
        rowData.isRowDisable = false; // Draft row: saved with Shipment/MAWB Save
      }
      const processedRow = this.processChargeData(rowData, parentDetails);
      allCharges.push(processedRow);
    });

    this.isInternalUpdate = true;
    if (allCharges.length > 0) {
      this.arChargesService.updateARChargesCollection(allCharges);
    }
    if (this.deletedArCharges.length > 0) {
      const processedDeletedCharges = this.deletedArCharges.map(deletedCharge =>
        this.processChargeData({ ...deletedCharge }, parentDetails)
      );
      this.arChargesService.updateDeletedARCharges(processedDeletedCharges);
    } else {
      this.arChargesService.updateDeletedARCharges([]);
    }
    this.isInternalUpdate = false;
  }

  private processChargeData(
    chargeData: any,
    parentDetails: any
  ): ArChargesFormObject {
    if (!chargeData.shipmentIdentifierId) {
      chargeData.shipmentIdentifierId = 0;
    }
    if (!chargeData.shipmentId && parentDetails.id) {
      chargeData.shipmentId = parentDetails.id;
    }
    if (!chargeData.shipmentIdentifierValue && parentDetails.ext) {
      chargeData.shipmentIdentifierValue = parentDetails.ext;
    }
    if (!chargeData.shipmentIdentifier) {
      chargeData.shipmentIdentifier = parentDetails.isFromHawb
        ? SingleDeleteHeaderText.Shipment
        : SingleDeleteHeaderText.MawbCaps;
    }
    if (!chargeData.action) {
      chargeData.action = this.commonService.isValidField(chargeData.id ?? 0)
        ? 'u'
        : 'c';
    }
    chargeData.id = this.commonService.isValidField(chargeData.id ?? 0)
      ? +(chargeData.id ?? 0)
      : 0;
    return chargeData;
  }

  private patchArChargesData() {
    const formArray = this.getArChargesFormRows();
    formArray.clear();
    if (!this.isInternalUpdate) {
      this.deletedArCharges = this.arChargesService.getDeletedArCharges();
    }
    const transformedData = this.arChargesService.getTransformedData();
    if (this.pageMode !== ModeIcons.view && transformedData.length === 0) {
      this.addInlineEditTableComp.addNewRow();
      this.inputRowChanges(0);
      this.addInlineEditTableComp.getTableData();
      this.changeDetectorRef.detectChanges();
      return;
    }
    if (this.pageMode !== ModeIcons.view && transformedData.length > 0) {
      this.addInlineEditTableComp.addNewRow();
      this.addInlineEditTableComp.loadAutocompleteForNewRow(0);
      this.inputRowChanges(0);
    }
    transformedData.forEach((charge, index) => {
      this.addInlineEditTableComp.addNewRow();
      const dataIndex = this.pageMode === ModeIcons.view ? index : index + 1;
      this.addInlineEditTableComp.loadAutocompleteForNewRow(dataIndex);
      const isRowDisabled = resolveChargeRowDisabledOnPatch(
        this.pageMode,
        dataIndex,
        charge.isRowDisable
      );
      const transformedCharge = { ...charge, isRowDisable: isRowDisabled };
      formArray.at(dataIndex).patchValue(transformedCharge);
      this.arChargesService.patchUnInvoicedInvoiceDate(formArray.at(dataIndex));
      if (isRowDisabled) {
        formArray.at(dataIndex).disable();
      } else {
        this.inputRowChanges(dataIndex);
      }
    });
    this.addInlineEditTableComp.getTableData();
    this.changeDetectorRef.detectChanges();
    if (
      this.pageMode !== ModeIcons.view &&
      this.arChargesService.getHasUnsavedChanges()
    ) {
      this.commonService.checkPristineAccordionData(false);
    }
    this.restoreChargeGridUiStateAfterPatch();
    this.updateTotalRow();
  }

  private restoreChargeGridUiStateAfterPatch(): void {
    if (this.pageMode === ModeIcons.view || !this.arChargesHeaderIcons) {
      return;
    }
    const rows = this.getArChargesFormRows();
    const hasEditableRow = rows.controls.some(
      control => control.get('isRowDisable')?.value === false
    );
    if (!hasEditableRow) {
      return;
    }
    this.isAddClicked = true;
    const firstEditable = rows.controls.find(
      control => control.get('isRowDisable')?.value === false
    );
    if (firstEditable) {
      this.onARChargesGridFormFieldChange(firstEditable.getRawValue());
    }
  }

  private inputRowChanges(index: number) {
    const formArray = this.getArChargesFormRows();
    formArray.at(index).enable();
    formArray.at(index).get('invoiceDate')?.disable();
    // PAC-24768 AC1: Published to GP is read-only; it follows the selected invoice.
    formArray.at(index).get('published')?.disable();
    this.arChargesService.applyInvoiceDateState(formArray.at(index));
    formArray.at(index).get('chargeDescription')?.disable();
  }

  private updateTotalRow(): void {
    const formRows = this.getArChargesFormRows();
    let total = 0;
    let hasData = false;

    // Includes draft rows (new rows are inserted at the top); blank rows have no amount.
    formRows.controls.forEach(control => {
      const rowValue = control.getRawValue();
      if (rowValue.chargeAmount) {
        hasData = true;
        const amount =
          typeof rowValue.chargeAmount === 'number'
            ? rowValue.chargeAmount
            : Number.parseFloat(String(rowValue.chargeAmount)) || 0;
        total += amount;
      }
    });

    this.showTotalRow = hasData;
    this.totalChargeAmount = total;
  }

  // ---------------------------------------------------------------------------
  // PAC-24768 — Invoice Number type-ahead (AC1) + Sync AP Invoices (AC3 / AC4)
  // ---------------------------------------------------------------------------

  private loadUnpublishedArInvoices(): void {
    this.arChargesService
      .getUnpublishedArInvoices()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((invoices: UnpublishedArInvoice[]) => {
        this.unpublishedArInvoicesById = new Map(
          invoices.map(invoice => [Number(invoice.id), invoice])
        );
        const options: IDropdownResponse[] = invoices.map(invoice => ({
          id: invoice.id,
          name: invoice.invoiceNumber,
          description: invoice.accountName ?? undefined,
        }));
        this.arChargesExternalDropdownData = {
          [AR_INVOICE_NUMBER_KEY]: options,
        };
        // Push the new list into the grid's @Input before wiring rows, else the first load is skipped.
        this.changeDetectorRef.detectChanges();
        this.addInlineEditTableComp?.initializeExternalDropdownsForAllRows();
        this.changeDetectorRef.detectChanges();
      });
  }

  /**
   * The generic autocomplete binds `arInvoiceId` from the picked option; Invoice Date and
   * Published to GP are read-only and must follow that selection (blank / No when cleared).
   */
  private syncInvoiceFieldsFromTypeahead(): void {
    if (!this.arChargesGridForm) {
      return;
    }
    for (const control of this.getArChargesFormRows().controls) {
      if (control.disabled || control.get('isRowDisable')?.value) {
        continue;
      }
      const row = control.getRawValue();
      const invoiceNumber = String(row.invoiceNumber ?? '').trim();
      const selected = this.findUnpublishedInvoiceByNumber(invoiceNumber);
      if (selected) {
        const patch = {
          arInvoiceId: selected.id,
          invoiceDate: selected.invoiceDate ?? '',
          published: popupButtons.No,
        };
        if (
          row.arInvoiceId !== patch.arInvoiceId ||
          row.invoiceDate !== patch.invoiceDate ||
          row.published !== patch.published
        ) {
          control.patchValue(patch, { emitEvent: false });
        }
      } else if (!invoiceNumber && (row.arInvoiceId || row.invoiceDate)) {
        control.patchValue(
          { arInvoiceId: null, invoiceDate: '', published: popupButtons.No },
          { emitEvent: false }
        );
      }
    }
  }

  private findUnpublishedInvoiceByNumber(
    invoiceNumber: string
  ): UnpublishedArInvoice | undefined {
    if (!invoiceNumber) {
      return undefined;
    }
    for (const invoice of this.unpublishedArInvoicesById.values()) {
      if (invoice.invoiceNumber === invoiceNumber) {
        return invoice;
      }
    }
    return undefined;
  }

  /** AC3: icon lives on the AR grid only in Add / Edit, only for a persisted Shipment (not MAWB). */
  private shouldShowSyncApInvoicesIcon(mode: string): boolean {
    if (mode !== ModeIcons.add && mode !== ModeIcons.edit) {
      return false;
    }
    const parent = this.arChargesService.getDetailsForARTab();
    return parent.isFromHawb && !!parent.id;
  }

  private appendSyncApInvoicesIcon(
    icons: SvgIconProp[],
    mode: string
  ): SvgIconProp[] {
    if (
      !this.shouldShowSyncApInvoicesIcon(mode) ||
      icons.some(icon => icon.name === ActionIcons.syncApInvoices)
    ) {
      return icons;
    }
    return [
      ...icons,
      {
        name: ActionIcons.syncApInvoices,
        isDisabled: true,
        width: 20,
        height: 20,
        order: 7,
        tooltip: ModalPopupMessages.syncApInvoicesHeader,
      },
    ];
  }

  private refreshSyncApEligibility(): void {
    const parent = this.arChargesService.getDetailsForARTab();
    if (!parent.isFromHawb || !parent.id) {
      this.syncApEligibility = null;
      this.applySyncApInvoicesIconState();
      return;
    }
    this.arChargesService
      .getSyncApChargesEligibility(parent.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        this.syncApEligibility =
          response?.statusCode === StatusCodes.Success ? response.data : null;
        this.applySyncApInvoicesIconState();
      });
  }

  private applySyncApInvoicesIconState(): void {
    const hasIcon = this.arChargesHeaderIcons?.some(
      item => item.name === ActionIcons.syncApInvoices
    );
    if (!hasIcon) {
      return;
    }
    const eligibility = this.syncApEligibility;
    const isAutoRated = !!eligibility?.isRated;
    const canSync =
      !!eligibility?.canSync &&
      isAutoRated &&
      !this.isSyncingApCharges &&
      !this.arChargesCrudLocked;
    let tooltip: string = ModalPopupMessages.syncApInvoicesHeader;
    if (eligibility?.isPublished) {
      tooltip = ModalPopupMessages.syncApInvoicesPublishedTooltip(
        eligibility.publishedInvoiceNumber ?? ''
      );
    } else if (eligibility && !eligibility.canSync) {
      tooltip = ModalPopupMessages.syncApInvoicesNoPendingTooltip;
    }
    // app-svg-icon only re-renders on a new `list` reference, so rebuild the array.
    this.arChargesHeaderIcons = this.arChargesHeaderIcons.map(
      (icon: SvgIconProp) =>
        icon.name === ActionIcons.syncApInvoices
          ? { ...icon, isDisabled: !canSync, tooltip }
          : icon
    );
    this.changeDetectorRef.detectChanges();
  }

  /** AC4: server adds only unsynced AP charges as unlinked AR rows; grid reloads via the host refresh. */
  private handleSyncApInvoicesClicked(): void {
    const parent = this.arChargesService.getDetailsForARTab();
    if (
      !parent.isFromHawb ||
      !parent.id ||
      !this.syncApEligibility?.canSync ||
      !this.syncApEligibility.isRated ||
      this.isSyncingApCharges
    ) {
      return;
    }
    this.isSyncingApCharges = true;
    this.applySyncApInvoicesIconState();
    this.arChargesService
      .syncApCharges(parent.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.isSyncingApCharges = false;
          if (response?.statusCode === StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              response.message,
              ModalPopupMessages.success
            );
            this.arChargeSaved.emit();
            return;
          }
          // 200 with body 204: nothing pending — informational, no refresh needed.
          this.snakbarService.openCustomisedSnackBar(
            response?.message ??
              ModalPopupMessages.syncApInvoicesNoPendingTooltip,
            ModalPopupMessages.Warning
          );
          this.refreshSyncApEligibility();
        },
        // Red toast already shown by errorHandler() in the service call.
        error: () => {
          this.isSyncingApCharges = false;
          this.refreshSyncApEligibility();
        },
      });
  }

  private checkMode(): void {
    this.commonService.isCheckForMode
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((data: string) => {
        if (data === ModeIcons.view) {
          this.pageMode = ModeIcons.view;
        } else {
          this.pageMode = data === '' ? ModeIcons.add : data;
        }
        this.getIconsForArChargesTableMode(this.pageMode);
        this.applyRpeFieldPermissions();
      });
  }
}
